<template>
  <div v-if="isOpen" class="fixed inset-0 z-[200] flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm overflow-y-auto" @click.self="$emit('close')">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto transform transition-all animate-in fade-in zoom-in duration-300 text-left relative">
      <!-- Modal Header -->
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between">
        <h2 class="text-lg font-bold m-0 text-white">User Details</h2>
        <button @click="$emit('close')" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <!-- Modal Body -->
      <div class="p-8">
        <div class="overflow-hidden border border-slate-200 rounded-lg">
          <table class="w-full text-sm text-left">
            <tbody class="divide-y divide-slate-100">
              <tr>
                <th class="px-6 py-4 bg-slate-50 font-semibold text-slate-700 w-1/3">Full Name</th>
                <td class="px-6 py-4 text-slate-600">{{ user?.full_name || '-' }}</td>
              </tr>
              <tr>
                <th class="px-6 py-4 bg-slate-50 font-semibold text-slate-700">Phone Number</th>
                <td class="px-6 py-4 text-slate-600">{{ user?.phone_number || '-' }}</td>
              </tr>
              <tr>
                <th class="px-6 py-4 bg-slate-50 font-semibold text-slate-700">Sex</th>
                <td class="px-6 py-4 text-slate-600">{{ user?.sex || '-' }}</td>
              </tr>
              <tr>
                <th class="px-6 py-4 bg-slate-50 font-semibold text-slate-700">Username</th>
                <td class="px-6 py-4 text-slate-600">{{ user?.username || '-' }}</td>
              </tr>
              <tr>
                <th class="px-6 py-4 bg-slate-50 font-semibold text-slate-700">Role</th>
                <td class="px-6 py-4 text-slate-600 uppercase tracking-tighter font-bold text-xs">{{ user?.role || '-' }}</td>
              </tr>
              <tr>
                <th class="px-6 py-4 bg-slate-50 font-semibold text-slate-700 align-top">Privileges</th>
                <td class="px-6 py-4">
                  <div v-if="user?.privilege" class="flex flex-wrap gap-2">
                    <template v-for="(actions, priv) in (typeof user.privilege === 'string' ? JSON.parse(user.privilege) : user.privilege)" :key="priv">
                      <div v-if="actions && typeof actions === 'object' && Object.values(actions).some(v => v)" class="text-xs bg-slate-100 px-2 py-1 rounded">
                        <strong class="text-[#0d4169]">{{ priv }}:</strong> 
                        <span class="text-slate-600 ml-1">
                          {{ Object.entries(actions).filter(([,v]) => v).map(([k]) => k).join(', ') }}
                        </span>
                      </div>
                    </template>
                  </div>
                  <span v-else class="text-slate-400">No privileges assigned</span>
                </td>
              </tr>
              <tr>
                <th class="px-6 py-4 bg-slate-50 font-semibold text-slate-700">Status</th>
                <td class="px-6 py-4">
                  <span class="status-pill" :class="user?.status === 1 ? 'active' : 'inactive'">
                    {{ user?.status === 1 ? 'Active' : 'Inactive' }}
                  </span>
                </td>
              </tr>
              <tr>
                <th class="px-6 py-4 bg-slate-50 font-semibold text-slate-700">Created At</th>
                <td class="px-6 py-4 text-slate-500 italic">{{ user?.created_at ? new Date(user.created_at).toLocaleString() : '-' }}</td>
              </tr>
              <tr>
                <th class="px-6 py-4 bg-slate-50 font-semibold text-slate-700">Registered By</th>
                <td class="px-6 py-4">
                  <div class="flex flex-col">
                    <span class="text-slate-700 font-bold">{{ user?.creator?.full_name || 'System' }}</span>
                    <span v-if="user?.creator?.role" class="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                      {{ user.creator.role }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr v-if="user?.updated_at || user?.updater">
                <th class="px-6 py-4 bg-slate-50 font-semibold text-slate-700">Last Updated At</th>
                <td class="px-6 py-4 text-slate-500 italic">{{ user?.updated_at ? new Date(user.updated_at).toLocaleString() : '-' }}</td>
              </tr>
              <tr v-if="user?.updater">
                <th class="px-6 py-4 bg-slate-50 font-semibold text-slate-700">Last Updated By</th>
                <td class="px-6 py-4">
                  <div class="flex flex-col">
                    <span class="text-slate-700 font-bold">{{ user.updater.full_name }}</span>
                    <span v-if="user.updater.role" class="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                      {{ user.updater.role }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-8 flex justify-end">
          <button 
            @click="$emit('close')" 
            class="px-8 py-2.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:transform active:scale-95"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue'

defineProps({
  isOpen: Boolean,
  user: { type: Object, default: null }
})

defineEmits(['close'])
</script>

<style scoped>
.status-pill {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: bold;
}
.active { background: #dcfce7; color: #166534; }
.inactive { background: #fee2e2; color: #991b1b; }

.animate-in {
  animation: 0.2s ease-out forwards;
}
.fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
.zoom-in {
  from { transform: scale(0.95); }
  to { transform: scale(1); }
}

table th {
  border-right: 1px solid #e2e8f0;
}
</style>
